// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { DebtBreakdownChart } from './debt-breakdown-chart';
import { DebtEvolutionChart } from './debt-evolution-chart';
import {
  KpiBoxes,
  PeriodPicker,
  Portal,
  SectionContainer,
} from '@shared/ui-kit/components';
import { Loader } from '../loader';
import { useLoans } from './use-loans';
import { RefreshControl } from '../refresh-control';
import { AccountsSelector } from '../accounts-selector';
import { LoansTable } from './loans-table';
import { CreditLinesTable } from './credit-lines-table';
import { NextAmortizationsTimeline } from './next-amortizations-timeline';

export const Loans = defineComponent({
  displayName: 'Loans',
  useSetup: useLoans,
  View: ({
    status,
    hasDebtData,
    currency,
    periodPicker,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    updatedDate,
    refreshMode,
    onRefresh,
    institutionId,
    t,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    kpis,
    debtChartData,
    hasDebtChartData,
    debtBreakdownByBank,
    debtBreakdownByType,
    loansTableData,
    hasLoansTableData,
    creditLinesTableData,
    hasCreditLinesTableData,
    nextAmortizations,
    hasNextAmortizationsSection,
    onAmortizationPeriodChange,
  }) => {
    return (
      <>
        <Portal container="aggregation-institutions-actions">
          <AccountsSelector
            data={accountOptions}
            onChange={onChangeAccounts}
            selectedAccounts={selectedAccounts}
            currency={currency}
            tags={tags}
            selectedTag={selectedTag}
            onTagSelect={setSelectedTag}
            tagsByObject={tagsByObject}
          />
        </Portal>

        <div className="flex flex-col gap-y-6" data-testid="loans-container">
          <div className="flex w-full flex-col items-stretch gap-y-3 md:flex-row md:items-center md:justify-between">
            <RefreshControl
              updatedDate={updatedDate}
              onRefresh={onRefresh}
              disabled={status === 'pending' || isSyncing}
              mode={refreshMode}
            />

            <div className="flex shrink-0 justify-end">
              <PeriodPicker
                onValueChange={periodPicker.onPeriodChange}
                defaultValue={periodPicker.defaultHist}
                availableOptions={['1M', '3M', '6M', '1Y']}
              />
            </div>
          </div>

          <Loader
            status={status}
            shouldDisplayEmptyState={!hasDebtData}
            institution={institutionId}
            onRefresh={onRefresh}
          >
            <div className="mb-4 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {kpis.map(kpi => (
                <KpiBoxes key={kpi.dataTestid ?? kpi.text} {...kpi} />
              ))}
            </div>

            {hasDebtChartData && (
              <div className="flex flex-col gap-6 xl:flex-row">
                <div className="flex-1">
                  <DebtEvolutionChart data={debtChartData} currency={currency} />
                </div>

                <div className="w-full flex-shrink-0 first:flex-1 xl:w-[420px]">
                  <DebtBreakdownChart
                    breakdownByBank={debtBreakdownByBank}
                    breakdownByType={debtBreakdownByType}
                  />
                </div>
              </div>
            )}

            {hasLoansTableData && (
              <SectionContainer
                title={t('glossary.loans', { ns: 'glossary' })}
                headerClass="mb-2"
              >
                <LoansTable data={loansTableData} currency={currency} />
              </SectionContainer>
            )}

            {hasCreditLinesTableData && (
              <SectionContainer
                title={t('glossary.lines-of-credit', { ns: 'glossary' })}
                headerClass="mb-2"
              >
                <CreditLinesTable data={creditLinesTableData} currency={currency} />
              </SectionContainer>
            )}

            {hasNextAmortizationsSection && (
              <SectionContainer
                title={t('glossary.next-amortizations', { ns: 'glossary' })}
                headerClass="mb-6"
                actions={
                  <PeriodPicker
                    availableOptions={['1M', '3M', '6M']}
                    defaultValue="3M"
                    onValueChange={onAmortizationPeriodChange}
                  />
                }
              >
                <NextAmortizationsTimeline
                  items={nextAmortizations}
                  currency={currency}
                />
              </SectionContainer>
            )}
          </Loader>
        </div>
      </>
    );
  },
});
